import React from 'react';
import PropTypes from 'prop-types';

import Button from 'components/Button/Button';
import FormGroup from 'components/FormGroup/FormGroup';
import FormItem from 'components/FormItem/FormItem';
import Input from 'components/Input/Input';
import Select from 'components/Select/Select';

import { ffd_versions, nds, taxes } from '../constants';
import WaitingRegistrationStep from './WaitingRegistrationStep';

import { validateReqiured } from 'utils/validate';

import styles from '../../terminal.scss';

const SetupStep = (props) => {
    const { terminalSettings, isLoading, onTerminalSave } = props;

    const [fields, setFields] = React.useState({
        taxation_type: {
            value: terminalSettings.taxation_type,
            validations: [validateReqiured],
        },
        ffd: {
            value: terminalSettings.ffd,
            validations: [validateReqiured],
        },
        vat: {
            value: terminalSettings.vat,
            validations: [validateReqiured],
        },
        return_url: {
            value: terminalSettings.return_url || 'https://dispurse.app/done',
            validations: [validateReqiured],
        },
    });

    function validateField(fieldName, value) {
        const field = fields[fieldName];
        const validations = field.validations;

        const errors = validations.map((validation) => validation(value)).filter((error) => Boolean(error));

        return errors[0];
    }

    function handleSelectChange(field, value) {
        const error = validateField(field, value);

        setFields((prev) => ({ ...prev, [field]: { ...prev[field], value, error } }));
    }

    function handleInputChange(e) {
        const value = e.target.value;
        const field = e.target.name;
        const error = validateField(field, value);

        setFields((prev) => ({ ...prev, [field]: { ...prev[field], value, error } }));
    }

    function prepareSettings() {
        const settings = {};

        for (const field in fields) {
            settings[field] = fields[field].value;
        }

        return settings;
    }

    function validateForm() {
        let errorsCount = 0;

        for (const field in fields) {
            const error = validateField(field, fields[field].value);

            if (error) {
                errorsCount++;
            }

            setFields((prev) => ({ ...prev, [field]: { ...prev[field], error } }));
        }

        return errorsCount;
    }

    function handleCompleteClick() {
        const errors = validateForm();

        if (errors > 0) {
            return false;
        }

        const settings = prepareSettings();
        settings.isYookassa = true;

        if (onTerminalSave) {
            /**
             * The second argument is Yookassa flag.
             */
            props.onTerminalSave({
                ...terminalSettings,
                ...settings,
            });
        }
    }

    return (
        <FormGroup>
            {terminalSettings.ready ? (
                <>
                    <FormItem>
                        <p className={styles.note}>
                            Вы успешно привязали свой магазин ЮКасса к Dispurse. Теперь можно изменить его конфигурацию.
                        </p>
                    </FormItem>
                    <FormItem required={true} label={'Система налогообложения'} error={fields.taxation_type.error}>
                        <Select
                            options={taxes}
                            onChange={(value) => handleSelectChange('taxation_type', value)}
                            defaultValue={fields.taxation_type.value}
                        ></Select>
                    </FormItem>
                    <FormItem required={true} label={'Версия ФФД'} error={fields.ffd.error}>
                        <Select
                            options={ffd_versions}
                            onChange={(value) => handleSelectChange('ffd', value)}
                            defaultValue={fields.ffd.value}
                        ></Select>
                    </FormItem>
                    <FormItem required={true} label={'НДС'} error={fields.vat.error}>
                        <Select
                            options={nds}
                            onChange={(value) => handleSelectChange('vat', value)}
                            defaultValue={fields.vat.value}
                        ></Select>
                    </FormItem>
                    <FormItem required={true} label={'Страница успешной оплаты'} error={fields.return_url.error}>
                        <Input
                            placeholder="https://dispurse.app/done"
                            name="return_url"
                            type="text"
                            onChange={handleInputChange}
                            value={fields.return_url.value}
                        />
                    </FormItem>
                    <FormItem>
                        <Button
                            isLoading={isLoading}
                            onClick={handleCompleteClick}
                            text="Сохранить"
                            level="secondary"
                        ></Button>
                    </FormItem>
                </>
            ) : (
                <WaitingRegistrationStep terminalSettings={terminalSettings} />
            )}
        </FormGroup>
    );
};

SetupStep.propTypes = {
    onTerminalSave: PropTypes.func,
    isLoading: PropTypes.bool,
    terminalSettings: PropTypes.shape({
        id: PropTypes.number,
        company_id: PropTypes.number,
        terminal_key: PropTypes.string,
        inn: PropTypes.string,
        owner_phone: PropTypes.string,
        return_url: PropTypes.string,
        ready: PropTypes.bool,
        registration_status: PropTypes.oneOf(['PENDING', 'MANUAL_BOARDING', 'SUCCEEDED']),
        taxation_type: PropTypes.oneOf(['osn', 'usn_income', 'usn_income_outcome', 'patent', 'envd', 'esn']),
        ffd: PropTypes.oneOf(['1.05', '1.2']),
        vat: PropTypes.oneOf(['none', 'vat0', 'vat5', 'vat10', 'vat18', 'vat110', 'vat118', 'vat120']),
    }),
};

export default SetupStep;
